import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import {
  toMonthCalendarEndDate,
  toMonthCalendarStartDate,
  toMonthEndDate,
  toMonthStartDate,
  toWeekEndDate,
  toWeekStartDate,
} from "../../helpers/moment";

import { distanceTo, isInsideCircle } from "geofencer";
import ClockInOut from "../../components/TimeClock/ClockInOut";
import ToastUtil from "../../components/ToastNotification/ToastUtil";
import { config } from "../../config/index";
import { POST, PUT, newFetch } from "../../helpers/fetch";
import { ATTENDANCE_STATUSES_ID } from "../../api/constants";
import "./styles.css";
const { API_URL } = config;

const TimeClock = ({ history, client, sites, location: { search } }) => {
  const { t } = useTranslation();
  const params = queryString.parse(search);
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user"));
  const activeSite = localStorage.getItem("default_site_id")
    ? Number(localStorage.getItem("default_site_id"))
    : null;

  /**
   * States
   */
  // Calendar State
  const [activeDate, setActiveDate] = useState(moment(params.date));
  const [activeWeek, setActiveWeek] = useState({});
  const [activeMonth, setActiveMonth] = useState({});

  const [siteSchedules, setSiteSchedules] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [activeSchedules, setActiveSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [attendanceStatuses, setAttendanceStatuses] = useState([]);
  const [showAllShifts, setShowAllShifts] = useState(false);
  const [siteShifts, setSiteShifts] = useState([]);
  const [preferredShifts, setPreferredShifts] = useState([]);
  const [defaultSiteData, setDefaultSiteData] = React.useState(null);
  const [isMultipleShiftAssign, setIsMultipleShiftAssign] =
    React.useState(false);
  const [totalHours, setTotalHours] = useState(0);
  const [updateTime, setUpdateTime] = useState(moment());
  const dateRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showMessage, setShowMessage] = React.useState(false);
  const [circleRadius, setCircleRadius] = useState({});

  // Get client site circle radius
  useEffect(() => {
    if (activeSite) {
      const selectedSite = filteredSites();
      if (selectedSite.length >= 0) {
        if (selectedSite[0] !== undefined) {
          const { lat, lng, distance } = selectedSite[0];

          const circle = {
            center: [Number(lat), Number(lng)], // Sample Location: Tanjong Pagar, Gouco Tower
            radius: Number(distance), // in km
          };

          setCircleRadius(circle);
        }
      }
    }
  }, [activeSite, sites]);

  // change active week
  useEffect(() => {
    if (client) {
      setActiveWeek({
        startDate: toWeekStartDate(moment(activeDate), client.start_day),
        endDate: toWeekEndDate(moment(activeDate), client.start_day),
      });

      setActiveMonth({
        startDate: toMonthStartDate(moment(activeDate)),
        endDate: toMonthEndDate(moment(activeDate)),
        calendarStartDate: toMonthCalendarStartDate(
          moment(activeDate),
          client.start_day
        ),
        calendarEndDate: toMonthCalendarEndDate(
          moment(activeDate),
          client.start_day
        ),
      });
    }
  }, [activeDate, client]);

  useEffect(() => {
    const fetchSiteSchedules = async () => {
      if (activeSite && activeWeek) {
        let url = `${API_URL}/site/${activeSite}/schedule`;
        try {
          let response = await fetch(url, {
            mode: "cors",
            method: "POST",
            withCredentials: true,
            body: JSON.stringify({
              start_date: moment(activeWeek.startDate).format("YYYY-MM-DD"),
              end_date: moment(activeWeek.endDate).format("YYYY-MM-DD"),
            }),
            headers: {
              Authorization: token,
              "Access-Control-Allow-Origin": "*",
              "Content-Type": "application/json; charset=utf-8",
            },
          });

          let data = await response.json();
          if (data.success) {
            let schedulesData = data.data;
            const currentWeekStartDate = toWeekStartDate(
              moment(),
              client.start_day
            );

            const selectedSite = filteredSites();
            if (
              //added client for enable br upload warning
              selectedSite &&
              selectedSite[0]?.enable_br_upload === false &&
              moment(activeWeek.startDate).isSameOrAfter(
                moment(currentWeekStartDate)
              ) &&
              moment(activeWeek.startDate).isSameOrBefore(
                toWeekEndDate(
                  moment().add(1, "weeks"),
                  client.start_day
                ).format("YYYY-MM-DD")
              )
            ) {
              if (
                schedulesData.length === 0 &&
                dateRef.current !==
                  moment(activeWeek.startDate).format("YYYY-MM-DD")
              ) {
                dateRef.current = moment(activeWeek.startDate).format(
                  "YYYY-MM-DD"
                );
                const api_path = `/site/${activeSite}/schedule/create`;
                const resp = await newFetch(POST, api_path, {
                  start_date: moment(activeWeek.startDate).format("YYYY-MM-DD"),
                  end_date: moment(activeWeek.endDate).format("YYYY-MM-DD"),
                  activeDate,
                });

                if (resp.success) {
                  schedulesData = resp.scheduleArr;
                }
                const filtered = schedulesData.reduce(
                  (result, option) =>
                    option.date === moment(activeDate).format("YYYY-MM-DD") &&
                    option.shift.site_id === activeSite
                      ? result.push({
                          schedule_id: option.id,
                          date: option.date,
                          end_time: option.shift.end_time,
                          shift: option.shift.name,
                          shift_id: option.shift_id,
                          site_id: option.shift.site_id,
                          start_time: option.shift.start_time,
                          //status: 1,
                          //...option.shift,
                        }) && result
                      : result,
                  []
                );
                setActiveSchedules(filtered);
              }
            }
            setSiteSchedules(schedulesData);
          } else {
            ToastUtil.error(data.message);
          }
        } catch (error) {
          ToastUtil.error(error);
        }
      }
    };

    fetchSiteSchedules();
  }, [activeWeek, activeSite, updateTime]);

  // Fetch Site Shifts
  useEffect(() => {
    const fetchSiteShifts = async () => {
      let url = `${API_URL}/site/${activeSite}/shifts`;

      try {
        let response = await fetch(url, {
          mode: "cors",
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: token,
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json charset=utf-8",
          },
        });

        let data = await response.json();

        if (data.success) {
          let shiftsData = data.data.sort(
            (a, b) =>
              moment(a.start_time, "HH:mm:ss") -
              moment(b.start_time, "HH:mm:ss")
          );

          if (activeSite === 1)
            shiftsData = moment(activeDate).isBefore(moment("2020-04-19"))
              ? shiftsData.filter((x) => [1, 2, 3, 7].includes(x.id))
              : shiftsData.filter((x) => x.id > 3);

          setSiteShifts(shiftsData);
        } else {
          ToastUtil.error(data.message);
        }
      } catch (error) {
        ToastUtil.error(error);
      }
    };

    if (activeSite) fetchSiteShifts();
  }, [activeSite, activeDate]);

  // Fetch User Preferred Shifts
  useEffect(() => {
    const fetchShiftPreference = async () => {
      setLoading(true);
      let url = `${API_URL}/associate/sites/${activeSite}/preference`;

      try {
        let response = await fetch(url, {
          mode: "cors",
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: token,
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json charset=utf-8",
          },
        });

        let data = await response.json();

        if (data.success) {
          let filtered = data.data
            .filter(
              (preference) =>
                preference.date === moment(activeDate).format("YYYY-MM-DD") &&
                preference.shift.site_id === activeSite
            )
            .sort(
              (a, b) =>
                moment(a.shift.start_time, "HH:mm:ss") -
                moment(b.shift.start_time, "HH:mm:ss")
            );
          setPreferredShifts(filtered);
        } else {
          ToastUtil.error(data.message);
        }
        setLoading(false);
        // data.success ? setSiteShifts(data.data) : ToastUtil.error(data.message)
      } catch (error) {
        ToastUtil.error(error);
      }
    };

    if (activeSite) fetchShiftPreference();
  }, [activeDate, activeSite, updateTime]);

  // Fetch Schedule
  useEffect(() => {
    const fetchSchedule = async () => {
      setLoading(true);
      if (defaultSiteData) {
        let api_path = `/associate/site/${activeSite}/assign_fixed_sched_date`;
        await newFetch(POST, api_path, {
          start_date: moment(activeWeek.startDate).format("YYYY-MM-DD"),
        });

        if (defaultSiteData?.enable_br_upload === false) {
          if (activeSite !== null) {
            let api_path = `/associate/site/${activeSite}/myschedule2`;
            try {
              const response = await newFetch(PUT, api_path, {
                start_date: moment(activeWeek.startDate).format("YYYY-MM-DD"),
                end_date: moment(activeWeek.endDate).format("YYYY-MM-DD"),
                activeDate,
              });
              if (response.data) {
                setSchedules(response.data);
              }
            } catch (error) {
              ToastUtil.error(error);
            }
          }
        } else {
          let url = `${API_URL}/associate/myschedule`;
          try {
            let response = await fetch(url, {
              mode: "cors",
              method: "GET",
              withCredentials: true,
              headers: {
                Authorization: token,
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json charset=utf-8",
              },
            });

            let data = await response.json();
            if (data.success) {
              const scheduleBySite = data.data.filter(
                (x) => x.site_id === activeSite
              );
              setSchedules(scheduleBySite);
            } else {
              ToastUtil.error(data.message);
            }
          } catch (error) {
            ToastUtil.error(error);
          }
        }
      }
      setLoading(false);
    };
    fetchSchedule();
  }, [activeSite, activeWeek, defaultSiteData]); //TODO: optimize this for the future

  // Active Schedule
  useEffect(() => {
    let clone = JSON.parse(JSON.stringify(schedules));
    let filtered = clone.filter(
      (schedule) => schedule.date === moment(activeDate).format("YYYY-MM-DD")
    );

    const selectedSite = filteredSites();

    if (selectedSite && selectedSite[0]?.enable_br_upload === false) {
      let has_schedule = filtered.some(
        (schedule) => schedule.has_schedule === 1
      );
      setShowAllShifts(!has_schedule);
    }

    if (filtered.length > 0) {
      setActiveSchedules(filtered);
    } else {
      setActiveSchedules([]);
    }
  }, [activeDate, activeSite, schedules, updateTime, sites]);

  useEffect(() => {
    const fetchTotalHours = async () => {
      let api_path = `/site/${activeSite}/associate/${user.id}/totalWorkHours`;
      let body = { date: moment(activeDate).format("YYYY-MM-DD") };
      let response = await newFetch(POST, api_path, body);

      if (response.success) {
        setTotalHours(response.data);
      } else {
        setTotalHours(0);
      }
    };

    if (activeSite && activeDate) fetchTotalHours();
  }, [activeSite, activeDate]);

  // Fetch Statuses
  useEffect(() => {
    const fetchStatuses = async () => {
      let url = `${API_URL}/attendanceStatus`;

      try {
        let response = await fetch(url, {
          mode: "cors",
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: token,
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json charset=utf-8",
          },
        });

        let data = await response.json();

        data.success
          ? setAttendanceStatuses(data.data)
          : ToastUtil.error(data.message);
      } catch (error) {
        ToastUtil.error(error);
      }
    };

    fetchStatuses();
  }, []);

  React.useEffect(() => {
    activeSchedules.length > 0 && selectedScheduleHandler();
  }, [activeSchedules]);

  React.useEffect(() => {
    sites && defaultSiteHandler();
  }, [sites]);

  React.useEffect(() => {
    if (!selectedSchedule) {
      const timer = setTimeout(() => {
        setShowMessage(true);
      }, 2000);

      return () => clearTimeout(timer); // Cleanup on unmount
    }
  }, [selectedSchedule]);

  const defaultSiteHandler = () =>
    setDefaultSiteData(sites.find((site) => site.id === activeSite));

  const selectedScheduleHandler = async () => {
    const assignedSchedule = activeSchedules
      .filter(
        (item) => item.status && item.status != ATTENDANCE_STATUSES_ID.CANCELLED
      )
      .map((schedule) => schedule);
    // more than one assigned show modal of Shift Selection
    if (assignedSchedule.length > 1) {
      setIsMultipleShiftAssign(true);

      const shiftHasCurrentTimeLogs = assignedSchedule.find(
        (item) => [2, 3, 4, 5].includes(item.status) && !item.time_out
      );
      // shift with current time_in but not yet time_out
      if (shiftHasCurrentTimeLogs) {
        setSelectedSchedule(shiftHasCurrentTimeLogs);
      }
    } else {
      if (assignedSchedule.length === 1) {
        setSelectedSchedule(assignedSchedule[0]);
      } else {
        // if no assign shift default 'Any'
        const noAssignedSchedule = activeSchedules.find(
          (item) => item.shift === "Any"
        );

        setSelectedSchedule(noAssignedSchedule);
      }
    }
  };

  const filteredSites = () => sites.filter((item) => item.id === activeSite);

  return (
    <React.Fragment>
      <div className="rounded-0 mb-0 dashboard__jumbotron-container timeclock-container">
        <div className="preferred__label-wrapper">
          <div>
            <NavLink
              to="/"
              className="text-decoration-none"
              style={{ color: "inherit" }}
            >
              <i
                className="fas fa-arrow-left"
                style={{
                  marginTop: 5,
                  paddingTop: "1rem",
                }}
              />
            </NavLink>
          </div>
          <div
            style={{
              marginLeft: 20,
              paddingTop: "1rem",
            }}
          >
            <h4 className="text-brand">{t("Timeclock")}</h4>
            <p className="lead">{moment().format("dddd, DD MMMM YYYY")}</p>
          </div>
        </div>
        <div style={{ margin: 15 }}>
          <h5>{defaultSiteData?.name || ""}</h5>
          {sites.length > 1 && (
            <p>
              <a href="/site-settings">{t("Change")}</a>
            </p>
          )}
        </div>
      </div>

      <div className="container">
        {loading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              marginTop: 150,
              gap: "10px",
            }}
          >
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="text-muted">{t("Loading...")}</p>
          </div>
        ) : showMessage && !selectedSchedule && !isMultipleShiftAssign ? (
          <p
            className="text-muted"
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 20,
            }}
          >
            {t(
              "No assigned shift found. Please inform back office team to assign a shift for you"
            )}
          </p>
        ) : (
          <ClockInOut
            history={history}
            activeSchedules={activeSchedules}
            activeSite={activeSite}
            selectedSchedule={selectedSchedule}
            setSelectedSchedule={setSelectedSchedule}
            activeClient={client}
            circleRadius={circleRadius}
            isInsideCircle={isInsideCircle}
            distanceTo={distanceTo}
            isMultipleShiftAssign={isMultipleShiftAssign}
            defaultSiteData={defaultSiteData}
            siteSchedules={siteSchedules}
            t={t}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default TimeClock;
